/**
 * Action type constants, display labels, behavior modes, and icon metadata.
 * Single source of truth for the frontend — mirrors backend handleActionsActivity.js.
 */

// ── ACTION TYPE ENUM ─────────────────────────────────────────────────────────

export const ACTION_TYPES = {
    SET_TICKET_TYPE_SUBTYPE: 'set_ticket_type_subtype',
    UPDATE_TICKET_SUMMARY: 'update_ticket_summary',
    SUGGEST_REPLY: 'suggest_reply',
    CHANGE_TICKET_STATUS: 'change_ticket_status',
    ADD_ASSET_CONTEXT: 'add_asset_context',
    SEND_TEAMS_NOTIFICATION: 'send_teams_notification',
    START_HUDU_PROCESS: 'start_hudu_process',
    SCREENCONNECT_EVENT: 'screenconnect_event',
    CLOSURE_ADD_FINAL_NOTE: 'closure_add_final_note',
    CLOSURE_RECORD_SOFTWARE_ASSET: 'closure_record_software_asset',
    CLOSURE_CREATE_KB_DRAFT: 'closure_create_kb_draft',
    CLOSURE_DOCUMENT_PROCESS_CHANGE: 'closure_document_process_change',
    CLOSURE_SEND_BULLETIN_NOTIFICATION: 'closure_send_bulletin_notification',
}

// ── HUMAN-READABLE LABELS ────────────────────────────────────────────────────

export const ACTION_LABELS = {
    [ACTION_TYPES.SET_TICKET_TYPE_SUBTYPE]: 'Set Type/Subtype',
    [ACTION_TYPES.UPDATE_TICKET_SUMMARY]: 'Update Subject',
    [ACTION_TYPES.SUGGEST_REPLY]: 'Suggested Reply',
    [ACTION_TYPES.CHANGE_TICKET_STATUS]: 'Change Status',
    [ACTION_TYPES.ADD_ASSET_CONTEXT]: 'Attach Asset',
    [ACTION_TYPES.SEND_TEAMS_NOTIFICATION]: 'Send Ticket Card to Teams',
    [ACTION_TYPES.START_HUDU_PROCESS]: 'Start Process',
    [ACTION_TYPES.SCREENCONNECT_EVENT]: 'ScreenConnect Event',
    [ACTION_TYPES.CLOSURE_ADD_FINAL_NOTE]: 'Add Final Note',
    [ACTION_TYPES.CLOSURE_RECORD_SOFTWARE_ASSET]: 'Record Software Asset',
    [ACTION_TYPES.CLOSURE_CREATE_KB_DRAFT]: 'Create KB Draft',
    [ACTION_TYPES.CLOSURE_DOCUMENT_PROCESS_CHANGE]: 'Document Process Change',
    [ACTION_TYPES.CLOSURE_SEND_BULLETIN_NOTIFICATION]: 'Post Bulletin',

}

// ── CLICK BEHAVIOR ───────────────────────────────────────────────────────────
// api_call     — Accept immediately fires resolve endpoint (no preview)
// modal_preview — Opens modal to preview content, then Accept/Dismiss
// modal_form   — Opens modal with form input before sending

export const ACTION_BEHAVIORS = {
    [ACTION_TYPES.SET_TICKET_TYPE_SUBTYPE]: 'api_call',
    [ACTION_TYPES.UPDATE_TICKET_SUMMARY]: 'modal_form',
    [ACTION_TYPES.SUGGEST_REPLY]: 'modal_form',
    [ACTION_TYPES.CHANGE_TICKET_STATUS]: 'api_call',
    [ACTION_TYPES.ADD_ASSET_CONTEXT]: 'api_call',
    [ACTION_TYPES.SEND_TEAMS_NOTIFICATION]: 'api_call',
    [ACTION_TYPES.START_HUDU_PROCESS]: 'modal_form',
    [ACTION_TYPES.SCREENCONNECT_EVENT]: 'api_call',
    [ACTION_TYPES.CLOSURE_ADD_FINAL_NOTE]: 'modal_preview',
    [ACTION_TYPES.CLOSURE_RECORD_SOFTWARE_ASSET]: 'modal_preview',
    [ACTION_TYPES.CLOSURE_CREATE_KB_DRAFT]: 'modal_preview',
    [ACTION_TYPES.CLOSURE_DOCUMENT_PROCESS_CHANGE]: 'modal_preview',
    [ACTION_TYPES.CLOSURE_SEND_BULLETIN_NOTIFICATION]: 'modal_preview',
}

// ── PRIORITY STYLES ──────────────────────────────────────────────────────────

export const PRIORITY_STYLES = {
    high: {
        border: 'border-l-red-500',
        badge: 'bg-red-100 text-red-700 border border-red-200',
        label: 'High',
    },
    medium: {
        border: 'border-l-yellow-500',
        badge: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
        label: 'Med',
    },
    low: {
        border: 'border-l-green-500',
        badge: 'bg-green-100 text-green-700 border border-green-200',
        label: 'Low',
    },
}

// ── ASSET SOURCE TYPES ───────────────────────────────────────────────────────

export const ASSET_SOURCES = {
    hudu_asset: { label: 'Hudu Asset', color: 'text-blue-600' },
    hudu_website: { label: 'Website', color: 'text-blue-600' },
    hudu_process: { label: 'Process', color: 'text-blue-600' },
    hudu_kb: { label: 'KB Article', color: 'text-blue-600' },
    cwm_ticket: { label: 'Ticket', color: 'text-indigo-600' },
    cwm_contact: { label: 'Contact', color: 'text-indigo-600' },
    cwm_company: { label: 'Company', color: 'text-indigo-600' },
}

// ── NOTIFICATION PRESETS ─────────────────────────────────────────────────────

export const NOTIFICATION_PRESETS = {
    notify_team: 'Notify Team',
    notify_escalation: 'Notify Escalation',
    notify_secops: 'Notify SecOps',
}

// ── HELPERS ──────────────────────────────────────────────────────────────────

/**
 * Safely parse the JSON payload value from an action, falling back to raw string.
 * @param {Object} action
 * @returns {Object|string|null}
 */
export const parsePayloadValue = (action) => {
    const raw = action?.payload?.value
    if (!raw) return null
    try {
        return JSON.parse(raw)
    } catch {
        return raw
    }
}

/**
 * Get compact card display text for an action, mirroring backend formatActionCard.
 * @param {Object} action
 * @returns {string}
 */
export const formatCardText = (action) => {
    const { type } = action
    const parsed = parsePayloadValue(action)

    switch (type) {
        case ACTION_TYPES.SET_TICKET_TYPE_SUBTYPE: {
            const parts = []
            if (typeof parsed === 'object') {
                if (parsed?.typeName) parts.push(`Type: ${parsed.typeName}`)
                if (parsed?.subtypeName) parts.push(`Subtype: ${parsed.subtypeName}`)
            }
            return parts.length ? `Set ${parts.join(' — ')}` : 'Set Type/Subtype (Error)'
        }
        case ACTION_TYPES.UPDATE_TICKET_SUMMARY:
            return 'Update Subject'
        case ACTION_TYPES.SUGGEST_REPLY:
            return 'Suggested Reply'
        case ACTION_TYPES.CHANGE_TICKET_STATUS:
            return `Change Status: ${typeof parsed === 'object' ? parsed?.statusName : parsed || '…'}`
        case ACTION_TYPES.ADD_ASSET_CONTEXT:
            return `Attach: ${typeof parsed === 'object' ? parsed?.assetName : action.title || 'Asset'}`
        case ACTION_TYPES.SEND_TEAMS_NOTIFICATION: {
            return 'Send Ticket Card to Teams'
        }
        case ACTION_TYPES.START_HUDU_PROCESS:
            return `Start Process: ${typeof parsed === 'object' ? parsed?.processName : '…'}`
        case ACTION_TYPES.SCREENCONNECT_EVENT:
            return `Recent Connection: ${typeof parsed === 'object' ? parsed?.computerName : (action.title || 'ScreenConnect (Error)')}`
        case ACTION_TYPES.CLOSURE_ADD_FINAL_NOTE:
        case ACTION_TYPES.CLOSURE_RECORD_SOFTWARE_ASSET:
        case ACTION_TYPES.CLOSURE_CREATE_KB_DRAFT:
        case ACTION_TYPES.CLOSURE_DOCUMENT_PROCESS_CHANGE:
        case ACTION_TYPES.CLOSURE_SEND_BULLETIN_NOTIFICATION:
            return action.title || ACTION_LABELS[type] || type
        default:
            return action.title || ACTION_LABELS[type] || type
    }
}

/**
 * Format a brief timeline description for an accepted action.
 * Mirrors backend formatTimelineEntry.
 * @param {Object} action - action with type and payload
 * @param {string} authorName
 * @returns {string}
 */
export const formatTimelineText = (action, authorName) => {
    const { type } = action
    const parsed = parsePayloadValue(action)
    const author = authorName || 'Unknown'

    switch (type) {
        case ACTION_TYPES.SET_TICKET_TYPE_SUBTYPE: {
            const tsParts = []
            if (typeof parsed === 'object') {
                if (parsed?.typeName) tsParts.push(parsed.typeName)
                if (parsed?.subtypeName) tsParts.push(parsed.subtypeName)
            }
            return `${author} Set Type to ${tsParts.join(' / ') || 'unknown'}`
        }
        case ACTION_TYPES.UPDATE_TICKET_SUMMARY:
            return `${author} Updated Subject`
        case ACTION_TYPES.SUGGEST_REPLY:
            return `${author} Reviewed Suggested Reply`
        case ACTION_TYPES.CHANGE_TICKET_STATUS: {
            const statusName = typeof parsed === 'object' ? parsed?.statusName : parsed
            return `${author} Changed Status to ${statusName || 'unknown'}`
        }
        case ACTION_TYPES.ADD_ASSET_CONTEXT: {
            const assetName = typeof parsed === 'object' ? parsed?.assetName : action.title
            return `${author} Attached ${assetName || 'Asset'}`
        }
        case ACTION_TYPES.SEND_TEAMS_NOTIFICATION: {
            return `${author} Sent Ticket Card to Teams`
        }
        case ACTION_TYPES.START_HUDU_PROCESS: {
            const processName = typeof parsed === 'object' ? parsed?.processName : null
            return `${author} Started Process: ${processName || 'Process'}`
        }
        case ACTION_TYPES.SCREENCONNECT_EVENT: {
            return `${author} ${action.title || type}`
        }
        case ACTION_TYPES.CLOSURE_ADD_FINAL_NOTE:
            return `${author} Reviewed Closure Note Recommendation: ${action.title || 'Add Final Note'}`
        case ACTION_TYPES.CLOSURE_RECORD_SOFTWARE_ASSET:
            return `${author} Reviewed Software Asset Recommendation: ${action.title || 'Record Software Asset'}`
        case ACTION_TYPES.CLOSURE_CREATE_KB_DRAFT:
            return `${author} Reviewed KB Draft Recommendation: ${action.title || 'Create KB Draft'}`
        case ACTION_TYPES.CLOSURE_DOCUMENT_PROCESS_CHANGE:
            return `${author} Reviewed Process Change Documentation: ${action.title || 'Document Process Change'}`
        case ACTION_TYPES.CLOSURE_SEND_BULLETIN_NOTIFICATION:
            return `${author} Sent Bulletin Notification: ${action.title || 'Post Bulletin'}`
        default:
            return `${author} performed action: ${action.title || type}`
    }
}
